// iter885 — Bank Reconciliation Cockpit
//
// One-page recon control tower that combines:
//   1. Live tenant-wide reconciliation-score dial (aggregate across all
//      recent statements — filterable by branch).
//   2. One-click "Boost Health" auto-matcher (calls
//      POST /api/bank-statements/boost-all-health).
//   3. The <ReconReviewQueueCard> drafts approve/reject widget from iter883.
//   4. Per-statement health table showing score + inline "Boost" per row.
//
// Works across all branches (see <BranchFilter> at top; empty = all).
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { BranchFilter } from '@/components/filters/BranchFilter';
import { ReconReviewQueueCard } from '@/components/banking/ReconReviewQueueCard';
import {
  Activity, TrendingUp, TrendingDown, Zap, RefreshCw, ExternalLink,
  AlertTriangle, CheckCircle2, Gauge, Download,
} from 'lucide-react';

// Circular score dial — pure SVG so we don't drag in a chart lib for
// what is a single ring. 0..100 → 220° arc from -110° to +110°.
function ScoreDial({ score, size = 180 }) {
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  const stroke = 14;
  const radius = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const arc = 220;
  const startAngle = 90 + (360 - arc) / 2;
  const endAngle = startAngle + arc;
  const progAngle = startAngle + (arc * s) / 100;

  const polar = (deg) => {
    const rad = (deg * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
  };
  const p0 = polar(startAngle);
  const p1 = polar(endAngle);
  const p2 = polar(progAngle);
  const largeBg = arc > 180 ? 1 : 0;
  const largeFg = arc * (s / 100) > 180 ? 1 : 0;

  const color = s >= 90 ? '#10b981' : s >= 70 ? '#eab308' : s >= 40 ? '#f97316' : '#ef4444';
  const label = s >= 90 ? 'Excellent' : s >= 70 ? 'Healthy' : s >= 40 ? 'Attention' : 'Critical';

  return (
    <div className="relative inline-flex items-center justify-center" data-testid="cockpit-score-dial">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <path
          d={`M ${p0.x} ${p0.y} A ${radius} ${radius} 0 ${largeBg} 1 ${p1.x} ${p1.y}`}
          fill="none" stroke="hsl(var(--muted))" strokeWidth={stroke} strokeLinecap="round"
        />
        <path
          d={`M ${p0.x} ${p0.y} A ${radius} ${radius} 0 ${largeFg} 1 ${p2.x} ${p2.y}`}
          fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          style={{ transition: 'all 0.8s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center pt-4">
        <span className="text-4xl font-bold tracking-tight" data-testid="cockpit-score-value">{s}</span>
        <span className="text-xs font-medium mt-1" style={{ color }}>{label}</span>
      </div>
    </div>
  );
}

export default function ReconCockpitPage() {
  const [branchId, setBranchId] = useState(null);
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [boosting, setBoosting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get('/recon-cockpit/unmatched-csv', {
        params: { branch_id: branchId || undefined, limit: 5000 },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'reconciliation-cockpit.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };
  const [rowBoosting, setRowBoosting] = useState({});
  // iter886 — Replace native window.confirm() with a stylable AlertDialog
  // for the "Boost Health across all statements" confirmation. Testable,
  // consistent with the rest of the app, and non-blocking.
  const [boostConfirmOpen, setBoostConfirmOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/bank-statements/health-summary');
      setSummaries(res.data?.summaries || []);
    } catch (e) {
      toast.error('Failed to load reconciliation health');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!branchId) return summaries;
    return summaries.filter((s) => (s.branch_id || null) === branchId);
  }, [summaries, branchId]);

  // Aggregate score: micro-average across all matched/total ratios.
  const aggregate = useMemo(() => {
    let total = 0; let matched = 0; let posSettled = 0;
    let unmatchedIn = 0; let unmatchedOut = 0;
    for (const s of filtered) {
      total += (s.total_txns || 0);
      matched += (s.matched || 0);
      posSettled += (s.pos_settled || 0);
      unmatchedIn += (s.unmatched_inbound || 0);
      unmatchedOut += (s.unmatched_outbound || 0);
    }
    const score = total > 0 ? Math.round((matched / total) * 100) : 100;
    return { total, matched, posSettled, unmatchedIn, unmatchedOut, score, statements: filtered.length };
  }, [filtered]);

  const boostAll = async () => {
    setBoostConfirmOpen(false);
    setBoosting(true);
    try {
      const res = await api.post('/bank-statements/boost-all-health', { score_below: 95, max_statements: 50 });
      const d = res.data || {};
      toast.success(
        `Processed ${d.processed || 0} statements · +${d.total_matches_added || 0} matches · Ø delta +${d.average_score_delta || 0}`,
        { duration: 8000 }
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Boost failed');
    } finally {
      setBoosting(false);
    }
  };

  const boostRow = async (stmtId) => {
    setRowBoosting((b) => ({ ...b, [stmtId]: true }));
    try {
      const res = await api.post(`/bank-statements/${stmtId}/boost-health`, {});
      const d = res.data || {};
      const delta = d.delta ?? ((d.after?.score || 0) - (d.before?.score || 0));
      toast.success(`Boosted +${delta} points · ${d.matches_added || 0} new matches`, { duration: 6000 });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Boost failed');
    } finally {
      setRowBoosting((b) => {
        const nb = { ...b };
        delete nb[stmtId];
        return nb;
      });
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-4 md:p-6" data-testid="recon-cockpit-page">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Gauge className="text-indigo-600" size={30} />
              Reconciliation Cockpit
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Live tenant health · one-click boost · review queue in one place.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <BranchFilter
              value={branchId}
              onChange={setBranchId}
              width="w-40"
              testid="cockpit-branch"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              className="gap-1"
              data-testid="cockpit-refresh-btn"
            >
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportCsv}
              disabled={exporting}
              className="gap-1"
              data-testid="cockpit-export-csv-btn"
            >
              <Download size={13} className={exporting ? 'animate-pulse' : ''} /> Export CSV
            </Button>
          </div>
        </div>

        {/* Top row: Score dial + KPIs + Boost */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-1" data-testid="cockpit-score-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity size={16} className="text-indigo-600" />
                Recon Health Score
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center pt-2">
              <ScoreDial score={aggregate.score} />
              <p className="text-xs text-muted-foreground mt-3" data-testid="cockpit-score-details">
                {aggregate.matched} of {aggregate.total} txns matched · {aggregate.statements} statement{aggregate.statements === 1 ? '' : 's'}
              </p>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2" data-testid="cockpit-kpi-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Snapshot</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-lg border border-border p-3 bg-emerald-50 dark:bg-emerald-950/20">
                  <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-300 font-medium">
                    <CheckCircle2 size={13} /> Matched
                  </div>
                  <div className="text-2xl font-semibold text-emerald-800 dark:text-emerald-200 mt-1" data-testid="cockpit-kpi-matched">
                    {aggregate.matched}
                  </div>
                </div>
                <div className="rounded-lg border border-border p-3 bg-sky-50 dark:bg-sky-950/20">
                  <div className="flex items-center gap-2 text-xs text-sky-700 dark:text-sky-300 font-medium">
                    <TrendingUp size={13} /> POS Settled
                  </div>
                  <div className="text-2xl font-semibold text-sky-800 dark:text-sky-200 mt-1" data-testid="cockpit-kpi-pos">
                    {aggregate.posSettled}
                  </div>
                </div>
                <div className="rounded-lg border border-border p-3 bg-amber-50 dark:bg-amber-950/20">
                  <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300 font-medium">
                    <TrendingDown size={13} /> Unmatched In
                  </div>
                  <div className="text-2xl font-semibold text-amber-800 dark:text-amber-200 mt-1" data-testid="cockpit-kpi-unmatched-in">
                    {aggregate.unmatchedIn}
                  </div>
                </div>
                <div className="rounded-lg border border-border p-3 bg-rose-50 dark:bg-rose-950/20">
                  <div className="flex items-center gap-2 text-xs text-rose-700 dark:text-rose-300 font-medium">
                    <AlertTriangle size={13} /> Unmatched Out
                  </div>
                  <div className="text-2xl font-semibold text-rose-800 dark:text-rose-200 mt-1" data-testid="cockpit-kpi-unmatched-out">
                    {aggregate.unmatchedOut}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2 items-center">
                <Button
                  onClick={() => setBoostConfirmOpen(true)}
                  disabled={boosting || aggregate.total === 0}
                  className="gap-2 bg-indigo-600 hover:bg-indigo-700"
                  data-testid="cockpit-boost-all-btn"
                >
                  <Zap size={14} className={boosting ? 'animate-pulse' : ''} />
                  {boosting ? 'Boosting…' : 'Boost Health (auto-match all)'}
                </Button>
                <Link to="/bank-statements">
                  <Button variant="outline" size="sm" className="gap-1" data-testid="cockpit-go-to-statements">
                    Bank statements <ExternalLink size={12} />
                  </Button>
                </Link>
                <Link to="/banking-hub">
                  <Button variant="outline" size="sm" className="gap-1" data-testid="cockpit-go-to-hub">
                    Banking hub <ExternalLink size={12} />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Review Queue */}
        <ReconReviewQueueCard />

        {/* Per-statement health table */}
        <Card data-testid="cockpit-statements-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center justify-between">
              <span>Per-Statement Health</span>
              <Badge variant="secondary" data-testid="cockpit-statement-count">
                {filtered.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <p className="text-sm text-muted-foreground p-4">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4" data-testid="cockpit-empty">
                No bank statements uploaded yet — head to Bank statements to add one.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Statement</th>
                      <th className="text-right px-3 py-2 font-medium">Total</th>
                      <th className="text-right px-3 py-2 font-medium">Matched</th>
                      <th className="text-right px-3 py-2 font-medium">Unmatched</th>
                      <th className="text-right px-3 py-2 font-medium">Score</th>
                      <th className="text-right px-3 py-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((s) => {
                      const scoreColor =
                        s.score >= 90 ? 'text-emerald-600' :
                        s.score >= 70 ? 'text-amber-600' :
                        s.score >= 40 ? 'text-orange-600' : 'text-rose-600';
                      return (
                        <tr
                          key={s.statement_id}
                          className="border-t border-border hover:bg-muted/30 transition-colors"
                          data-testid={`cockpit-row-${s.statement_id}`}
                        >
                          <td className="px-4 py-2">
                            <div className="font-medium truncate max-w-[240px]">
                              {s.bank_name || s.file_name || s.statement_id.slice(0, 8)}
                            </div>
                            {s.label && (
                              <div className="text-[10px] text-muted-foreground mt-0.5">{s.label}</div>
                            )}
                          </td>
                          <td className="text-right px-3 py-2 font-mono">{s.total_txns || 0}</td>
                          <td className="text-right px-3 py-2 font-mono text-emerald-600">{s.matched || 0}</td>
                          <td className="text-right px-3 py-2 font-mono text-rose-600">
                            {(s.unmatched_inbound || 0) + (s.unmatched_outbound || 0)}
                          </td>
                          <td className={`text-right px-3 py-2 font-mono font-semibold ${scoreColor}`} data-testid={`cockpit-row-score-${s.statement_id}`}>
                            {s.score ?? 0}
                          </td>
                          <td className="text-right px-3 py-2">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={!!rowBoosting[s.statement_id] || s.score >= 95}
                              onClick={() => boostRow(s.statement_id)}
                              className="h-7 gap-1"
                              data-testid={`cockpit-row-boost-${s.statement_id}`}
                            >
                              <Zap size={11} /> Boost
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* iter886 — Confirm dialog for Boost Health tenant-wide action. */}
        <AlertDialog open={boostConfirmOpen} onOpenChange={setBoostConfirmOpen}>
          <AlertDialogContent data-testid="cockpit-boost-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Boost Health across all statements?</AlertDialogTitle>
              <AlertDialogDescription>
                This runs the auto-matcher across every statement below 95% health.
                It will attempt to auto-book eligible high-confidence transactions and
                may create Supplier Payment / Partner Ledger drafts for review.
                <br /><br />
                Nothing is destructive — you can still approve or reject drafts in the
                Recon Review Queue below. Continue?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cockpit-boost-confirm-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={boostAll}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="cockpit-boost-confirm-run"
              >
                Yes, run Boost
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
